import { create } from 'zustand';
import { supabase } from '../supabase';
import type { ServiceAddon, ServiceAddonFormData } from '../types';

// Alcuni componenti importano i tipi da qui: il tipo vero vive in ../types.
export type { ServiceAddon, ServiceAddonFormData } from '../types';

// =====================================================
// serviceAddonsStore
// Store Zustand per gestire gli add-on master del catalogo servizi e prodotti.
// Vedi piano: MD/plans/2026-05-30-service-addons-plan.md
// Estensione xor service/product: migration 20260605_service_addons_product_support.sql
//
// Pattern accumulativo: i fetch di target diversi NON si sovrascrivono, ma vengono uniti.
// =====================================================

interface ServiceAddonsState {
  addons: ServiceAddon[];
  loading: boolean;
  error: string | null;

  // Actions (xor target: passa serviceId OPPURE productId, mai entrambi)
  fetchAddonsByService: (serviceId: string) => Promise<ServiceAddon[]>;
  fetchAddonsByProduct: (productId: string) => Promise<ServiceAddon[]>;
  createAddon: (data: ServiceAddonFormData) => Promise<ServiceAddon>;
  updateAddon: (id: string, data: Partial<ServiceAddonFormData>) => Promise<void>;
  deleteAddon: (id: string) => Promise<void>;
  reorderAddons: (targetId: string, orderedIds: string[], targetKind?: 'service' | 'product') => Promise<void>;
  getAddonsByServiceId: (serviceId: string) => ServiceAddon[];
  getAddonsByProductId: (productId: string) => ServiceAddon[];
  clearAddonsForService: (serviceId: string) => void;
  clearAddonsForProduct: (productId: string) => void;
  clearAll: () => void;
}

/**
 * `service_addons.billing_prices` esiste dalla migration
 * 20260813_billing_prices_per_frequenza.sql. Su un database dove non è ancora
 * applicata PostgREST risponde PGRST204 / 42703: in quel caso si salva senza
 * gli sconti per frequenza invece di perdere tutto il salvataggio.
 */
function isMissingBillingPrices(error: any): boolean {
  const code = error?.code ?? '';
  const message = error?.message ?? '';
  return (code === 'PGRST204' || code === '42703') && message.includes('billing_prices');
}

const fetchByColumn = async (column: 'service_id' | 'product_id', value: string): Promise<ServiceAddon[]> => {
  const { data, error } = await supabase
    .from('service_addons')
    .select('*')
    .eq(column, value)
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []) as ServiceAddon[];
};

export const useServiceAddonsStore = create<ServiceAddonsState>((set, get) => ({
  addons: [],
  loading: false,
  error: null,

  fetchAddonsByService: async (serviceId: string) => {
    set({ loading: true, error: null });
    try {
      const fetched = await fetchByColumn('service_id', serviceId);
      // Accumula: rimuovi gli add-on del servizio corrente e aggiungi i nuovi
      set((state) => ({
        addons: [
          ...state.addons.filter((a) => a.service_id !== serviceId),
          ...fetched,
        ],
        loading: false,
      }));
      return fetched;
    } catch (err: any) {
      console.error('❌ serviceAddonsStore.fetchAddonsByService error:', err);
      set({ error: err.message || 'Errore nel caricamento degli add-on', loading: false });
      return [];
    }
  },

  fetchAddonsByProduct: async (productId: string) => {
    set({ loading: true, error: null });
    try {
      const fetched = await fetchByColumn('product_id', productId);
      set((state) => ({
        addons: [
          ...state.addons.filter((a) => a.product_id !== productId),
          ...fetched,
        ],
        loading: false,
      }));
      return fetched;
    } catch (err: any) {
      console.error('❌ serviceAddonsStore.fetchAddonsByProduct error:', err);
      set({ error: err.message || 'Errore nel caricamento degli add-on', loading: false });
      return [];
    }
  },

  createAddon: async (data: ServiceAddonFormData) => {
    set({ loading: true, error: null });
    try {
      // XOR validation
      const hasService = !!data.service_id;
      const hasProduct = !!data.product_id;
      if (hasService === hasProduct) {
        throw new Error('Add-on: indicare esattamente uno tra service_id e product_id');
      }

      // Risolvi l'organization dell'utente corrente
      const { data: userRes, error: userErr } = await supabase.auth.getUser();
      if (userErr) throw userErr;
      const userId = userRes.user?.id;
      if (!userId) throw new Error('Utente non autenticato');

      const { data: profile, error: profileErr } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userId)
        .single();
      if (profileErr) throw profileErr;

      // Calcola next sort_order tra add-on dello stesso target
      const existing = get().addons.filter((a) =>
        hasService ? a.service_id === data.service_id : a.product_id === data.product_id
      );
      const nextSortOrder = data.sort_order ?? (existing.length > 0
        ? Math.max(...existing.map((a) => a.sort_order ?? 0)) + 1
        : 0);

      const payload = {
        service_id: hasService ? data.service_id : null,
        product_id: hasProduct ? data.product_id : null,
        organization_id: profile.organization_id,
        name: data.name,
        description: data.description ?? null,
        amount: data.amount ?? 0,
        is_required: data.is_required ?? false,
        is_included_in_base: data.is_included_in_base ?? false,
        // Colonne 20260812: incluse solo se valorizzate, così l'insert resta
        // valido anche su un DB dove la migration gruppi non è ancora applicata.
        ...(data.addon_group !== undefined ? { addon_group: data.addon_group } : {}),
        ...(data.addon_group_exclusive !== undefined
          ? { addon_group_exclusive: data.addon_group_exclusive }
          : {}),
        ...(data.billing_prices !== undefined ? { billing_prices: data.billing_prices } : {}),
        // Sotto-opzioni (20260814): fascia di un add-on padre.
        ...(data.parent_addon_id !== undefined ? { parent_addon_id: data.parent_addon_id } : {}),
        ...(data.is_default_option !== undefined ? { is_default_option: data.is_default_option } : {}),
        sort_order: nextSortOrder,
        is_active: data.is_active ?? true,
        created_by: userId,
      };

      let { data: inserted, error } = await supabase
        .from('service_addons')
        .insert([payload])
        .select()
        .single();
      if (error && isMissingBillingPrices(error)) {
        console.warn('⚠️ service_addons.billing_prices assente: creo senza sconti per frequenza');
        const { billing_prices: _dropped, ...withoutBillingPrices } = payload as any;
        ({ data: inserted, error } = await supabase
          .from('service_addons')
          .insert([withoutBillingPrices])
          .select()
          .single());
      }
      if (error) throw error;

      set((state) => ({
        addons: [...state.addons, inserted as ServiceAddon],
        loading: false,
      }));
      return inserted as ServiceAddon;
    } catch (err: any) {
      console.error('❌ serviceAddonsStore.createAddon error:', err);
      set({ error: err.message || 'Errore creazione add-on', loading: false });
      throw err;
    }
  },

  updateAddon: async (id: string, data: Partial<ServiceAddonFormData>) => {
    set({ loading: true, error: null });
    try {
      // Non permettere di cambiare target via update (xor è scolpito alla creazione)
      const { service_id: _s, product_id: _p, ...safeData } = data;
      let { error } = await supabase
        .from('service_addons')
        .update(safeData)
        .eq('id', id);
      if (error && isMissingBillingPrices(error)) {
        console.warn('⚠️ service_addons.billing_prices assente: salvo senza sconti per frequenza');
        const { billing_prices: _dropped, ...withoutBillingPrices } = safeData as any;
        ({ error } = await supabase
          .from('service_addons')
          .update(withoutBillingPrices)
          .eq('id', id));
      }
      if (error) throw error;

      set((state) => ({
        addons: state.addons.map((a) => (a.id === id ? { ...a, ...safeData } as ServiceAddon : a)),
        loading: false,
      }));
    } catch (err: any) {
      console.error('❌ serviceAddonsStore.updateAddon error:', err);
      set({ error: err.message || 'Errore aggiornamento add-on', loading: false });
      throw err;
    }
  },

  deleteAddon: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('service_addons')
        .delete()
        .eq('id', id);
      if (error) throw error;

      set((state) => ({
        addons: state.addons.filter((a) => a.id !== id),
        loading: false,
      }));
    } catch (err: any) {
      console.error('❌ serviceAddonsStore.deleteAddon error:', err);
      set({ error: err.message || 'Errore eliminazione add-on', loading: false });
      throw err;
    }
  },

  reorderAddons: async (targetId: string, orderedIds: string[], targetKind: 'service' | 'product' = 'service') => {
    set({ loading: true, error: null });
    try {
      await Promise.all(
        orderedIds.map((id, idx) =>
          supabase.from('service_addons').update({ sort_order: idx }).eq('id', id)
        )
      );

      set((state) => ({
        addons: state.addons.map((a) => {
          const matches = targetKind === 'service' ? a.service_id === targetId : a.product_id === targetId;
          if (!matches) return a;
          const idx = orderedIds.indexOf(a.id);
          return idx === -1 ? a : { ...a, sort_order: idx };
        }),
        loading: false,
      }));
    } catch (err: any) {
      console.error('❌ serviceAddonsStore.reorderAddons error:', err);
      set({ error: err.message || 'Errore riordino add-on', loading: false });
      throw err;
    }
  },

  getAddonsByServiceId: (serviceId: string) => {
    return get().addons
      .filter((a) => a.service_id === serviceId && a.is_active)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  },

  getAddonsByProductId: (productId: string) => {
    return get().addons
      .filter((a) => a.product_id === productId && a.is_active)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  },

  clearAddonsForService: (serviceId: string) => {
    set((state) => ({
      addons: state.addons.filter((a) => a.service_id !== serviceId),
    }));
  },

  clearAddonsForProduct: (productId: string) => {
    set((state) => ({
      addons: state.addons.filter((a) => a.product_id !== productId),
    }));
  },

  clearAll: () => {
    set({ addons: [], error: null });
  },
}));
